import autoTable from "jspdf-autotable";
import { deriveCallReviewSections } from "./callReviewScoring";

const HEADER_FILL = [31, 93, 117]; // #1f5d75 (teal)
const HEADER_TEXT = [255, 255, 255];
const ROW_FILL = [238, 241, 244]; // #eef1f4
const ROW_ALT_FILL = [227, 231, 235]; // #e3e7eb
const TOTAL_ROW_FILL = [214, 221, 227]; // #d6dde3
const BORDER_COLOR = [208, 215, 222]; // #d0d7de
const TEXT_COLOR = [51, 51, 51]; // #333

const BASE_STYLES = {
  fontSize: 9,
  cellPadding: 3,
  overflow: "linebreak", // wrap long "Scoring Category" text instead of clipping it
  valign: "middle",
  lineColor: BORDER_COLOR,
  lineWidth: 0.2,
  textColor: TEXT_COLOR,
};

const HEAD_STYLES = {
  fillColor: HEADER_FILL,
  textColor: HEADER_TEXT,
  fontStyle: "bold",
  halign: "left",
};

const markTotalRow = (rowIndex, totalRowIndexes) =>
  totalRowIndexes.has(rowIndex);

export const renderCallReviewAutoTables = ({
  doc,
  startY,
  margin = 14,
  callReviewData,
  onPage,
}) => {
  const derived = deriveCallReviewSections(callReviewData);
  if (!derived) return startY;

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;

  const { utSection, sSection, oSection, totals, average, totalScore5 } =
    derived.summary;

  const pct = (v) => `${(v ?? 0).toFixed(1)}%`;
  const num = (v) => (v ?? 0).toFixed(2);

  // A fully-N/A section has a weighted max of 0 (nothing scored). Show "—" for
  // its Achieved / Score-out-of-5 / % cells instead of a misleading 0 / 0%.
  const sectionRow = (label, s = {}) => {
    const max = s.max ?? 0;
    return max === 0
      ? [label, max, "—", "—", "—"]
      : [label, max, num(s.achieved), num(s.outOf5), pct(s.percentage)];
  };

  const allNA = (totals.max ?? 0) === 0;
  const summaryBody = [
    sectionRow("Using the Tools", utSection),
    sectionRow("Sales", sSection),
    sectionRow("Objections", oSection),
    [
      "Total",
      totals.max ?? 0,
      allNA ? "—" : num(totals.achieved),
      allNA ? "—" : totalScore5.toFixed(2),
      "",
    ],
    [
      "Average Score",
      "",
      "",
      allNA ? "—" : num(average.outOf5),
      allNA ? "—" : `${(average.percentage ?? 0).toFixed(2)}%`,
    ],
  ];
  const summaryTotalRows = new Set([3, 4]);

  let cursorY = startY;

  autoTable(doc, {
    startY: cursorY,
    margin: { left: margin, right: margin, top: margin, bottom: margin },
    head: [
      [
        "Scoring Category",
        "Max Score",
        "Achieved Score",
        "Score out of 5",
        "% Score",
      ],
    ],
    body: summaryBody,
    theme: "grid",
    styles: BASE_STYLES,
    headStyles: HEAD_STYLES,
    bodyStyles: { fillColor: ROW_FILL },
    alternateRowStyles: { fillColor: ROW_ALT_FILL },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.32 },
    },
    tableWidth: contentWidth,
    showHead: "everyPage",
    didParseCell: (data) => {
      if (
        data.section === "body" &&
        markTotalRow(data.row.index, summaryTotalRows)
      ) {
        data.cell.styles.fillColor = TOTAL_ROW_FILL;
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: (data) => {
      if (typeof onPage === "function") onPage(data);
    },
  });
  cursorY = doc.lastAutoTable.finalY + 10;

  const detailSections = [
    derived.usingTools,
    derived.sales,
    derived.objections,
  ];

  const MIN_SECTION_SPACE_MM = 45;

  detailSections.forEach((section) => {
    if (
      cursorY + MIN_SECTION_SPACE_MM >
      doc.internal.pageSize.getHeight() - margin
    ) {
      doc.addPage();
      cursorY = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...TEXT_COLOR);
    doc.text(section.title, margin, cursorY);
    cursorY += 5;

    const isNA = (v) => v === "NA";
    const totalMax = section.maxValues.reduce(
      (s, v, i) => s + (isNA(section.values[i]) ? 0 : Number(v) || 0),
      0,
    );
    const totalAchieved = section.values.reduce(
      (s, v, i) =>
        s +
        (isNA(v) ? 0 : (Number(v) || 0) * (section.weightageFactor[i] ?? 1)),
      0,
    );

    const body = section.fields.map((label, idx) => {
      const v = section.values[idx];
      if (isNA(v)) {
        return [
          label,
          "N/A",
          (section.weightageFactor[idx] ?? 1).toFixed(2),
          "—",
          "—",
        ];
      }
      return [
        label,
        v ?? 0,
        (section.weightageFactor[idx] ?? 1).toFixed(2),
        (section.maxValues[idx] ?? 0).toFixed(2),
        ((Number(v) || 0) * (section.weightageFactor[idx] ?? 1)).toFixed(2),
      ];
    });
    body.push(["Total", "", "", totalMax.toFixed(2), totalAchieved.toFixed(2)]);
    const totalRowIndex = new Set([body.length - 1]);

    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin, top: margin },
      head: [
        [
          "Scoring Category",
          "Score",
          "Weightage Factor",
          "Max Score",
          "Achieved Score",
        ],
      ],
      body,
      theme: "grid",
      styles: BASE_STYLES,
      headStyles: HEAD_STYLES,
      bodyStyles: { fillColor: ROW_FILL },
      alternateRowStyles: { fillColor: ROW_ALT_FILL },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.46 },
      },
      tableWidth: contentWidth,
      showHead: "everyPage",
      didParseCell: (data) => {
        if (
          data.section === "body" &&
          markTotalRow(data.row.index, totalRowIndex)
        ) {
          data.cell.styles.fillColor = TOTAL_ROW_FILL;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: (data) => {
        if (typeof onPage === "function") onPage(data);
      },
    });

    cursorY = doc.lastAutoTable.finalY + 10;
  });

  return cursorY;
};

export default renderCallReviewAutoTables;
